import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, Box, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Link as MuiLink } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

// Custom centered loading overlay component
const CenteredLoadingOverlay = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
        }}
    >
        <CircularProgress size={60} />
    </Box>
);

const BatterAttributesTable = ({ apiUrl }) => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Pagination state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [rowCount, setRowCount] = useState(0);

    // State for filters
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPosition, setSelectedPosition] = useState('');
    const [selectedSeason, setSelectedSeason] = useState('');
    const [selectedArchetype, setSelectedArchetype] = useState('');

    // State for dropdown options
    const [teamOptions, setTeamOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [seasonOptions, setSeasonOptions] = useState([]);
    const [archetypeOptions, setArchetypeOptions] = useState([]);

    // Ref to track if initial load is complete
    const initialLoadComplete = useRef(false);
    const filterOptionsLoaded = useRef(false);

    const baseballPositionOrder = useRef(['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF']);

    // Build query parameters for API call
    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();

        // Pagination
        params.append('page', paginationModel.page + 1);
        params.append('page_size', paginationModel.pageSize);

        // Search
        if (searchTerm) {
            params.append('search', searchTerm);
        }

        // Filters
        if (selectedTeam) params.append('team', selectedTeam);
        if (selectedPosition) params.append('position', selectedPosition);
        if (selectedSeason) params.append('season', selectedSeason);
        if (selectedArchetype) params.append('archetype', selectedArchetype);

        return params;
    }, [paginationModel, searchTerm, selectedTeam, selectedPosition, selectedSeason, selectedArchetype]);

    // Fetch filter options once on mount
    const fetchFilterOptions = useCallback(async () => {
        if (filterOptionsLoaded.current) return;

        try {
            // Remove /paginated from the URL for filter-options endpoint
            const filterOptionsUrl = apiUrl.replace('/paginated', '/filter-options');
            const response = await fetch(filterOptionsUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const filterOptions = await response.json();
            
            if (filterOptions.teams) {
                setTeamOptions(filterOptions.teams);
            }
            
            if (filterOptions.positions) {
                const positions = filterOptions.positions;
                positions.sort((a, b) => {
                    const indexA = baseballPositionOrder.current.indexOf(a);
                    const indexB = baseballPositionOrder.current.indexOf(b);
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return a.localeCompare(b);
                });
                setPositionOptions(positions);
            }
            
            if (filterOptions.seasons) {
                setSeasonOptions(filterOptions.seasons);
            }
            
            if (filterOptions.archetypes) {
                setArchetypeOptions(filterOptions.archetypes);
            }

            filterOptionsLoaded.current = true;
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    }, [apiUrl]);

    // Fetch data with server-side pagination
    const fetchData = useCallback(async () => {
        setLoading(true);
        setPlayers([]); // Clear data immediately to show loading state

        try {
            const params = buildQueryParams();
            const url = `${apiUrl}?${params.toString()}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();

            // Handle paginated response
            const data = responseData.data || responseData;
            const paginationInfo = responseData.pagination || {};

            const playersWithIds = data.map((player, index) => ({
                ...player,
                id: player.uid || index,
                usernameDisplay: player.user ? player.user.username : 'N/A',
                user_url: player.user?.url || null,
                team_name: player.team ? player.team.team_name : 'N/A',
                // Flatten batting attributes for easier access
                hitting_archetype: player.batting_attributes?.hitting_archetype || 'N/A',
                babip_vs_lhp: player.batting_attributes?.babip_vs_lhp,
                babip_vs_rhp: player.batting_attributes?.babip_vs_rhp,
                avoid_ks_vs_lhp: player.batting_attributes?.avoid_ks_vs_lhp,
                avoid_ks_vs_rhp: player.batting_attributes?.avoid_ks_vs_rhp,
                gap_vs_lhp: player.batting_attributes?.gap_vs_lhp,
                gap_vs_rhp: player.batting_attributes?.gap_vs_rhp,
                power_vs_lhp: player.batting_attributes?.power_vs_lhp,
                power_vs_rhp: player.batting_attributes?.power_vs_rhp,
                eye_patience_vs_lhp: player.batting_attributes?.eye_patience_vs_lhp,
                eye_patience_vs_rhp: player.batting_attributes?.eye_patience_vs_rhp,
                speed: player.batting_attributes?.speed,
                stealing_ability: player.batting_attributes?.stealing_ability,
                bunting: player.batting_attributes?.bunting,
                fielding_range: player.batting_attributes?.fielding_range,
                fielding_error: player.batting_attributes?.fielding_error,
                fielding_arm: player.batting_attributes?.fielding_arm,
                turn_double_play: player.batting_attributes?.turn_double_play,
                catcher_blocking: player.batting_attributes?.catcher_blocking,
                catcher_framing: player.batting_attributes?.catcher_framing,
                position_1: player.batting_attributes?.position_1,
                position_2: player.batting_attributes?.position_2,
                position_3: player.batting_attributes?.position_3,
                total_hitting_type_changes: player.batting_attributes?.total_hitting_type_changes,
            }));

            setPlayers(playersWithIds);
            setRowCount(paginationInfo.total_count || data.length);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError(error);
            setLoading(false);
        }
    }, [apiUrl, buildQueryParams]);

    // Load filter options once on mount
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // Initial load and refetch when dependencies change with debouncing
    useEffect(() => {
        if (!initialLoadComplete.current) {
            fetchData();
            initialLoadComplete.current = true;
            return;
        }

        // Debounce for search and filter changes (500ms)
        const timeoutId = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, searchTerm, selectedTeam, selectedPosition, selectedSeason, selectedArchetype]);

    const createDropdown = (label, value, setValue, options) => (
        <FormControl sx={{ minWidth: 150, marginRight: 2 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={value}
                label={label}
                onChange={(e) => setValue(e.target.value)}
            >
                <MenuItem value="">All</MenuItem>
                {options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const downloadCSV = async () => {
        setDownloading(true);

        try {
            // Calculate total pages needed
            const totalPages = Math.ceil(rowCount / 100); // Use max page size of 100
            const allPlayers = [];

            // Fetch all pages
            for (let page = 1; page <= totalPages; page++) {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                params.append('page_size', '100');

                // Apply current filters
                if (searchTerm) params.append('search', searchTerm);
                if (selectedTeam) params.append('team', selectedTeam);
                if (selectedPosition) params.append('position', selectedPosition);
                if (selectedSeason) params.append('season', selectedSeason);
                if (selectedArchetype) params.append('archetype', selectedArchetype);

                const response = await fetch(`${apiUrl}?${params.toString()}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                const data = responseData.data || responseData;

                allPlayers.push(...data.map(player => ({
                    ...player,
                    usernameDisplay: player.user ? player.user.username : 'N/A',
                    user_url: player.user?.url || null,
                    team_name: player.team ? player.team.team_name : 'N/A',
                    hitting_archetype: player.batting_attributes?.hitting_archetype || 'N/A',
                    babip_vs_lhp: player.batting_attributes?.babip_vs_lhp,
                    babip_vs_rhp: player.batting_attributes?.babip_vs_rhp,
                    avoid_ks_vs_lhp: player.batting_attributes?.avoid_ks_vs_lhp,
                    avoid_ks_vs_rhp: player.batting_attributes?.avoid_ks_vs_rhp,
                    gap_vs_lhp: player.batting_attributes?.gap_vs_lhp,
                    gap_vs_rhp: player.batting_attributes?.gap_vs_rhp,
                    power_vs_lhp: player.batting_attributes?.power_vs_lhp,
                    power_vs_rhp: player.batting_attributes?.power_vs_rhp,
                    eye_patience_vs_lhp: player.batting_attributes?.eye_patience_vs_lhp,
                    eye_patience_vs_rhp: player.batting_attributes?.eye_patience_vs_rhp,
                    speed: player.batting_attributes?.speed,
                    stealing_ability: player.batting_attributes?.stealing_ability,
                    bunting: player.batting_attributes?.bunting,
                    fielding_range: player.batting_attributes?.fielding_range,
                    fielding_error: player.batting_attributes?.fielding_error,
                    fielding_arm: player.batting_attributes?.fielding_arm,
                    turn_double_play: player.batting_attributes?.turn_double_play,
                    catcher_blocking: player.batting_attributes?.catcher_blocking,
                    catcher_framing: player.batting_attributes?.catcher_framing,
                    position_1: player.batting_attributes?.position_1,
                    position_2: player.batting_attributes?.position_2,
                    position_3: player.batting_attributes?.position_3,
                })));
            }

            // Generate CSV
            const headers = [
                'Player Name', 'Username', 'Number', 'Pos', 'Team', 'Bats', 'Hitting Type',
                'TPE', 'TPE Banked', 'Season', 'Archetype',
                'BABIP vs LHP', 'Avoid Ks vs LHP', 'Gap vs LHP', 'Power vs LHP', 'Eye/Pat vs LHP',
                'BABIP vs RHP', 'Avoid Ks vs RHP', 'Gap vs RHP', 'Power vs RHP', 'Eye/Pat vs RHP',
                'Speed', 'Stealing', 'Bunting',
                'Fld Range', 'Fld Error', 'Fld Arm', 'Turn DP', 'C Blocking', 'C Framing',
                'Pos 1', 'Pos 2', 'Pos 3', 'Hitting Type Changes'
            ];

            const csvData = allPlayers.map(player => [
                player.player_name,
                player.usernameDisplay,
                player.number,
                player.position,
                player.team_name,
                player.bats,
                player.hitting_type,
                player.tpe,
                player.tpe_banked,
                player.season,
                player.hitting_archetype,
                player.babip_vs_lhp,
                player.avoid_ks_vs_lhp,
                player.gap_vs_lhp,
                player.power_vs_lhp,
                player.eye_patience_vs_lhp,
                player.babip_vs_rhp,
                player.avoid_ks_vs_rhp,
                player.gap_vs_rhp,
                player.power_vs_rhp,
                player.eye_patience_vs_rhp,
                player.speed,
                player.stealing_ability,
                player.bunting,
                player.fielding_range,
                player.fielding_error,
                player.fielding_arm,
                player.turn_double_play,
                player.catcher_blocking,
                player.catcher_framing,
                player.position_1,
                player.position_2,
                player.position_3,
                player.batting_attributes?.total_hitting_type_changes
            ].map(cell => {
                const cellStr = cell?.toString() || '';
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(','));

            const csvContent = [headers.join(','), ...csvData].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `batter_attributes_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Error downloading CSV. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const columns = useMemo(() => [
        {
            field: 'player_name',
            headerName: 'Player Name',
            width: 180,
            frozen: true,
            renderCell: (params) => (
                params.row.url ? (
                    <MuiLink
                        href={params.row.url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : params.value
            ),
        },
        {
            field: 'usernameDisplay',
            headerName: 'Username',
            width: 130,
            renderCell: (params) => (
                params.row.user_url ? (
                    <MuiLink
                        href={params.row.user_url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : params.value
            ),
        },
        { field: 'number', headerName: 'Number', width: 80, type: 'number' },
        { field: 'position', headerName: 'Pos', width: 70 },
        { field: 'team_name', headerName: 'Team', width: 180 },
        { field: 'bats', headerName: 'Bats', width: 90 },
        { field: 'hitting_type', headerName: 'Hitting Type', width: 120 },
        { field: 'tpe', headerName: 'TPE', width: 90, type: 'number' },
        { field: 'tpe_banked', headerName: 'TPE Banked', width: 110, type: 'number' },
        { field: 'season', headerName: 'Season', width: 90, type: 'number' },
        { field: 'hitting_archetype', headerName: 'Archetype', width: 220 },

        // vs LHP
        { field: 'babip_vs_lhp', headerName: 'BABIP vs LHP', width: 120, type: 'number' },
        { field: 'avoid_ks_vs_lhp', headerName: 'Avoid Ks vs LHP', width: 140, type: 'number' },
        { field: 'gap_vs_lhp', headerName: 'Gap vs LHP', width: 120, type: 'number' },
        { field: 'power_vs_lhp', headerName: 'Power vs LHP', width: 130, type: 'number' },
        { field: 'eye_patience_vs_lhp', headerName: 'Eye/Pat vs LHP', width: 140, type: 'number' },

        // vs RHP
        { field: 'babip_vs_rhp', headerName: 'BABIP vs RHP', width: 120, type: 'number' },
        { field: 'avoid_ks_vs_rhp', headerName: 'Avoid Ks vs RHP', width: 140, type: 'number' },
        { field: 'gap_vs_rhp', headerName: 'Gap vs RHP', width: 120, type: 'number' },
        { field: 'power_vs_rhp', headerName: 'Power vs RHP', width: 130, type: 'number' },
        { field: 'eye_patience_vs_rhp', headerName: 'Eye/Pat vs RHP', width: 140, type: 'number' },

        // Speed
        { field: 'speed', headerName: 'Speed', width: 90, type: 'number' },
        { field: 'stealing_ability', headerName: 'Stealing', width: 100, type: 'number' },
        { field: 'bunting', headerName: 'Bunting', width: 100, type: 'number' },

        // Fielding
        { field: 'fielding_range', headerName: 'Fld Range', width: 110, type: 'number' },
        { field: 'fielding_error', headerName: 'Fld Error', width: 110, type: 'number' },
        { field: 'fielding_arm', headerName: 'Fld Arm', width: 100, type: 'number' },
        { field: 'turn_double_play', headerName: 'Turn DP', width: 100, type: 'number' },
        { field: 'catcher_blocking', headerName: 'C Blocking', width: 120, type: 'number' },
        { field: 'catcher_framing', headerName: 'C Framing', width: 120, type: 'number' },

        // Positions
        { field: 'position_1', headerName: 'Pos 1', width: 80 },
        { field: 'position_2', headerName: 'Pos 2', width: 80 },
        { field: 'position_3', headerName: 'Pos 3', width: 80 },
        { field: 'total_hitting_type_changes', headerName: 'Hitting Type Changes', width: 170, type: 'number' },
    ], []);

    if (error) return <div>Error loading data: {error.message}</div>;

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 2, alignItems: 'center' }}>
                <TextField
                    label="Search by Player or Username"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 250 }}
                />
                {createDropdown("Team", selectedTeam, setSelectedTeam, teamOptions)}
                {createDropdown("Position", selectedPosition, setSelectedPosition, positionOptions)}
                {createDropdown("Season", selectedSeason, setSelectedSeason, seasonOptions)}
                {createDropdown("Archetype", selectedArchetype, setSelectedArchetype, archetypeOptions)}
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={downloadCSV}
                    disabled={downloading}
                    sx={{ height: 56 }}
                >
                    {downloading ? 'Downloading...' : 'Download CSV'}
                </Button>
            </Box>
            <div style={{ height: 600, width: '100%', position: 'relative' }}>
                <DataGrid
                    rows={players}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    rowCount={rowCount}
                    paginationMode="server"
                    loading={loading}
                    disableSelectionOnClick
                    slots={{
                        loadingOverlay: CenteredLoadingOverlay,
                    }}
                />
            </div>
        </Box>
    );
};

export default BatterAttributesTable;
